import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Copy, RefreshCw } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { supabase } from "@/integrations/supabase/client";

interface MFAEnrollProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export function MFAEnroll({ onSuccess, onCancel }: MFAEnrollProps) {
  const { enrollMFA, verifyMFA } = useAuth();
  const [enrollmentData, setEnrollmentData] = useState<{
    id: string;
    uri: string;
    secret: string;
  } | null>(null);
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    startEnrollment();
  }, []);

  const startEnrollment = async () => {
    setIsLoading(true);
    try {
      // Clean up any existing unverified factors that may have been left
      // from a previous incomplete enrollment attempt. Supabase returns 422
      // if a factor with the same friendly name already exists.
      const { data: existingFactors } = await supabase.auth.mfa.listFactors();
      if (existingFactors) {
        const unverifiedFactors = existingFactors.all.filter(
          (f) => f.factor_type === "totp" && f.status === "unverified"
        );
        for (const factor of unverifiedFactors) {
          await supabase.auth.mfa.unenroll({ factorId: factor.id });
        }
      }

      const { data, error } = await enrollMFA();
      if (error) {
        toast.error(error.message || "Failed to start MFA enrollment");
        setIsLoading(false);
        return;
      }

      // Extract the base32 secret from the otpauth URI for manual entry
      const secretMatch = data.totp.uri.match(/secret=([A-Z2-7]+)/i);
      const secret = secretMatch ? secretMatch[1] : "";

      setEnrollmentData({
        id: data.id,
        uri: data.totp.uri,
        secret,
      });
      setAttempts(0);
    } catch (err: any) {
      toast.error(err.message || "Failed to start MFA enrollment");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!enrollmentData || code.length !== 6 || isLoading) return;

    setIsLoading(true);
    try {
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: enrollmentData.id,
      });

      if (challengeError) throw challengeError;

      const { error: verifyError } = await verifyMFA(
        enrollmentData.id,
        challenge.id,
        code
      );

      if (verifyError) {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        setCode("");

        if (newAttempts >= 3) {
          toast.error(
            "Code invalid. Please check: (1) your phone's clock is set to automatic/network time, (2) you're using the correct authenticator entry. Try 'Generate New QR' if the problem persists.",
            { duration: 8000 }
          );
        } else if (newAttempts >= 2) {
          toast.error(
            "Invalid code. Make sure your phone's time is synced automatically (Settings → Date & Time → Set Automatically).",
            { duration: 6000 }
          );
        } else {
          toast.error("Invalid code. Please wait for a new code and try again.");
        }
      } else {
        toast.success("MFA enabled successfully! Your account is now secured.");
        onSuccess();
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to verify MFA");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !enrollmentData) {
    return (
      <div className="flex flex-col items-center py-6 gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Setting up authenticator...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 flex flex-col items-center">
      <div className="flex items-center justify-center">
        <div className="p-2 rounded-full bg-amber-100 text-amber-600">
          <ShieldCheck className="h-6 w-6" />
        </div>
      </div>

      <div className="text-center space-y-2">
        <h2 className="text-xl font-semibold">Set Up Two-Factor Authentication</h2>
        <p className="text-sm text-muted-foreground">
          MFA is required for all admin accounts. Scan the QR code below with your authenticator app
          (Google Authenticator, Authy, etc.)
        </p>
      </div>

      {enrollmentData && (
        <>
          <div className="p-4 bg-white rounded-xl shadow-inner border">
            <QRCodeSVG value={enrollmentData.uri} size={200} />
          </div>

          {/* Manual secret key for users who can't scan */}
          {enrollmentData.secret && (
            <div className="w-full max-w-xs">
              <p className="text-xs text-muted-foreground text-center mb-1">Can't scan? Enter this key manually:</p>
              <button
                type="button"
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-muted rounded-lg text-xs font-mono tracking-wider select-all hover:bg-muted/80 transition-colors"
                onClick={() => {
                  navigator.clipboard.writeText(enrollmentData.secret);
                  toast.success("Secret key copied to clipboard");
                }}
              >
                {enrollmentData.secret}
                <Copy className="h-3 w-3 shrink-0 text-muted-foreground" />
              </button>
            </div>
          )}
        </>
      )}

      <form onSubmit={handleVerify} className="space-y-4 w-full flex flex-col items-center">
        <div className="text-center space-y-2">
          <p className="text-sm font-medium">Verify Setup</p>
          <p className="text-xs text-muted-foreground">
            Enter the 6-digit code from your authenticator app
          </p>
        </div>

        <InputOTP
          maxLength={6}
          value={code}
          onChange={(value) => setCode(value)}
          onComplete={() => handleVerify()}
          autoFocus
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>

        <div className="flex flex-col w-full gap-2">
          <Button type="submit" className="w-full" disabled={isLoading || code.length !== 6}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirm & Enable MFA
          </Button>
          {attempts >= 2 && (
            <Button
              variant="outline"
              type="button"
              className="w-full"
              disabled={isLoading}
              onClick={() => {
                setEnrollmentData(null);
                setCode("");
                startEnrollment();
              }}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Generate New QR Code
            </Button>
          )}
          <Button variant="ghost" type="button" onClick={onCancel} className="w-full">
            Sign Out
          </Button>
        </div>
      </form>
    </div>
  );
}
